import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpTag } from '@hp/ui';
import { useCan, useMe } from '../../api/me';
import { trpc } from '../../api/trpc';
import { invalidateEnrichment, type EnrichmentMatch, type FirmographicCandidate } from './queries';
import { StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { PdLink } from '../../components/pd-link';
import { RelativeTime } from '../../components/relative-time';
import { fmtInt } from '../../lib/format';

export function MatchCard({ match }: { match: EnrichmentMatch }) {
  const { t } = useTranslation(['enrichment', 'common']);
  const me = useMe();
  const canReview = useCan()('crm.review');
  const pending = match.status === 'pending';
  const [selectedIco, setSelectedIco] = useState<string | null>(match.candidates[0]?.ico ?? null);
  const providerLabel = t(`provider.${match.provider}`, { defaultValue: match.provider });

  const decide = useMutation(trpc.enrichment.decide.mutationOptions({ onSuccess: () => invalidateEnrichment() }));

  function accept() {
    if (!selectedIco || decide.isPending) return;
    const cand = match.candidates.find((c) => c.ico === selectedIco);
    if (!cand) return;
    if (cand.dissolved && !window.confirm(t('match.confirmDissolved', { name: cand.name }))) return;
    decide.mutate({ matchId: match.id, action: 'accept', ico: cand.ico });
  }

  function acceptProfile() {
    const profile = match.profiles[0];
    if (!profile || decide.isPending) return;
    decide.mutate({ matchId: match.id, action: 'accept', ...(profile.domain ? { ref: profile.domain } : {}) });
  }

  function reject() {
    if (decide.isPending) return;
    decide.mutate({ matchId: match.id, action: 'reject' });
  }

  return (
    <article className="bo-match">
      {/* Organization header */}
      <div className="bo-match__head">
        <strong>{match.orgName ?? t('match.unnamedOrg')}</strong>
        <PdLink domain={me.data?.pipedriveCompanyDomain} entity="organization" pdId={match.orgPdId} />
        {match.orgAddress && <span className="bo-muted">{match.orgAddress}</span>}
        <span className="bo-grow-end bo-row">
          {match.provider !== 'ares' && <HpTag tone="info">{providerLabel}</HpTag>}
          <span className="bo-chip">{t('common:count.deals', { count: match.openDeals })}</span>
          <span className="bo-chip">{t('common:count.people', { count: match.peopleCount })}</span>
          {!pending && <StatusBadge status={match.status} />}
        </span>
      </div>

      {/* Outcome line for decided matches */}
      {!pending && (
        <p className="bo-match__outcome">
          {(match.status === 'accepted' || match.status === 'auto_accepted') && (
            <>
              {t('match.ico')} <strong className="bo-mono">{match.acceptedIco ?? '—'}</strong>
              {match.acceptedName ? ` · ${match.acceptedName}` : ''}
              {' · '}
            </>
          )}
          {match.status === 'no_match' && (
            <>
              {match.provider === 'ares'
                ? t('match.noAresMatch')
                : t('match.noProviderMatch', { provider: providerLabel })}
              {' · '}
            </>
          )}
          {match.status === 'rejected' && (
            <>
              {t('match.allRejected')}
              {' · '}
            </>
          )}
          {match.decidedByEmail ?? (match.status === 'auto_accepted' ? t('common:misc.auto') : '')}{' '}
          <RelativeTime value={match.decidedAt ?? match.updatedAt} className="bo-muted" />
        </p>
      )}

      {/* Firmographic profile (apollo/lusha) */}
      {match.provider !== 'ares' &&
        match.profiles.map((profile, i) => <FirmoProfile key={profile.domain ?? i} profile={profile} />)}
      {match.provider !== 'ares' && match.profiles.length === 0 && (
        <p className="bo-match__empty">{t('match.noProfile')}</p>
      )}

      {/* Candidates (ares) */}
      {match.provider === 'ares' && match.candidates.length > 0 ? (
        <ul className="bo-candidates">
          {match.candidates.map((cand) => {
            const isChosen = !pending && match.acceptedIco === cand.ico;
            const cls = [
              'bo-candidate',
              pending ? 'is-pickable' : '',
              pending && selectedIco === cand.ico ? 'is-selected' : '',
              isChosen ? 'is-chosen' : '',
            ]
              .join(' ')
              .trim();
            return (
              <li key={cand.ico}>
                <label className={cls}>
                  {pending && (
                    <input
                      type="radio"
                      name={`match-${match.id}`}
                      checked={selectedIco === cand.ico}
                      onChange={() => setSelectedIco(cand.ico)}
                    />
                  )}
                  {isChosen && <HpTag tone="success">{t('match.chosen')}</HpTag>}
                  <strong>{cand.name}</strong>
                  <span className="bo-mono">{cand.ico}</span>
                  {(cand.address ?? cand.city) && (
                    <span className="bo-muted">
                      {[cand.address, cand.city].filter((part) => part !== null && part !== '').join(', ')}
                    </span>
                  )}
                  <span className="bo-grow-end bo-row">
                    {cand.dissolved && <HpTag tone="danger">{t('match.dissolved')}</HpTag>}
                    <span className="bo-mono bo-strong">{Math.round(cand.score * 100)} %</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : match.provider === 'ares' ? (
        <p className="bo-match__empty">{t('match.noCandidates')}</p>
      ) : null}

      {/* Decision buttons — pending only */}
      {pending && canReview && (
        <div className="bo-match__actions">
          <HpButton
            variant="primary"
            className="bo-button--sm"
            icon="check"
            busy={decide.isPending && decide.variables?.action === 'accept'}
            disabled={(match.provider === 'ares' ? !selectedIco : match.profiles.length === 0) || decide.isPending}
            onClick={match.provider === 'ares' ? accept : acceptProfile}
          >
            {t('common:actions.accept')}
          </HpButton>
          <HpButton
            className="bo-button--sm bo-danger"
            icon="close"
            busy={decide.isPending && decide.variables?.action === 'reject'}
            disabled={decide.isPending}
            onClick={reject}
          >
            {t('match.rejectAll')}
          </HpButton>
          <ErrorAlert error={decide.error} className="bo-grow" />
        </div>
      )}
    </article>
  );
}

/** Company profile from Apollo/Lusha: the whole profile is accepted or rejected as one. */
function FirmoProfile({ profile }: { profile: FirmographicCandidate }) {
  const { t } = useTranslation(['enrichment', 'common']);
  const facts: Array<[string, React.ReactNode]> = [];
  if (profile.industry) facts.push([t('profile.industry'), profile.industry]);
  if (profile.employeeCount !== null) facts.push([t('profile.employees'), fmtInt(profile.employeeCount)]);
  if (profile.annualRevenue !== null) facts.push([t('profile.revenue'), `$${fmtInt(profile.annualRevenue)}`]);
  if (profile.foundedYear !== null) facts.push([t('profile.founded'), profile.foundedYear]);
  if (profile.phone) facts.push([t('profile.phone'), <span className="bo-mono">{profile.phone}</span>]);
  if (profile.address) facts.push([t('profile.address'), profile.address]);

  return (
    <div className="bo-profile">
      <div className="bo-row">
        <strong>{profile.name ?? '—'}</strong>
        {profile.domain && (
          <a
            href={profile.websiteUrl ?? `https://${profile.domain}`}
            target="_blank"
            rel="noreferrer"
            className="hp-detail-action hp-detail-action--text"
          >
            {profile.domain} ↗
          </a>
        )}
        {profile.linkedinUrl && (
          <a
            href={profile.linkedinUrl}
            target="_blank"
            rel="noreferrer"
            className="hp-detail-action hp-detail-action--text"
          >
            LinkedIn ↗
          </a>
        )}
        <span className="bo-grow-end bo-row">
          <HpTag
            tone={profile.queriedBy === 'name' ? 'danger' : 'neutral'}
            title={t(`profile.queriedBy.${profile.queriedBy}`)}
          >
            {profile.queriedBy === 'name' ? t('profile.nameOnly') : t('profile.byDomain')}
          </HpTag>
          <span className="bo-mono bo-strong">{Math.round(profile.score * 100)} %</span>
        </span>
      </div>
      {facts.length > 0 && (
        <dl className="bo-facts">
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {profile.description && <p className="bo-muted">{profile.description}</p>}
    </div>
  );
}
